import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import { createColumnHelper } from '@tanstack/react-table';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { VOUCHER_USAGE_BADGE_OPTIONS } from 'src/features/voucherTypes/utils/constants';
import { useListVouchers } from 'src/features/vouchers/api/useListVouchers';
import { Voucher } from 'src/features/vouchers/utils/types';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { vouchersMessages } from 'src/features/vouchers/utils/messages';
import { customersMessages } from 'src/features/customers/utils/messages';
import { useParams } from 'react-router-dom';
import CreateVoucherFormModal from 'src/features/vouchers/components/forms/CreateVoucherFormModal';
import CellVoucherActions from 'src/features/vouchers/components/tables/CellVoucherActions';
import useSorting from 'src/registry/hooks/useSorting';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const VouchersTable = () => {
    const { formatMessage, formatNumber } = useIntl();
    const { customerId } = useParams();

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'issued_at', desc: false }],
    });

    const permissionManageVouchers = useHasPermissions([PERMISSION.MANAGE_VOUCHERS]);
    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);
    const permissionAccessVouchers = useHasPermissions([PERMISSION.ACCESS_VOUCHERS]);

    const {
        data: vouchers,
        isLoading,
        isError,
    } = useListVouchers({
        ...(customerId && { customer: customerId }),
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...querySortingOptions,
    });

    const columnHelper = createColumnHelper<Voucher>();

    const columns = [
        columnHelper.accessor('code', {
            header: DataTableColumnHeader,
            cell: (ctx) => <CellLinkWithIcon {...ctx} to={`/vouchers/${ctx.row.original.id}`} />,
            meta: {
                title: formatMessage(voucherTypesMessages.code),
            },
        }),
        columnHelper.accessor('voucher_type.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/voucher-types/${ctx.row.original.voucher_type.id}`}
                        hasPermissions={permissionAccessVouchers}
                    />
                );
            },
            meta: {
                title: formatMessage(voucherTypesMessages.voucherType),
            },
        }),
        ...(!customerId
            ? [
                  columnHelper.accessor('customer.name', {
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      cell: (ctx) => {
                          return (
                              <CellLinkWithIcon
                                  {...ctx}
                                  to={`/customers/${ctx.row.original.customer?.id}`}
                                  hasPermissions={permissionAccessCustomers}
                              />
                          );
                      },
                      meta: {
                          title: formatMessage(customersMessages.customer),
                      },
                  }),
              ]
            : []),
        columnHelper.accessor('issued_at', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(vouchersMessages.issuedAt),
            },
        }),
        columnHelper.accessor('expires_at', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(vouchersMessages.expiresAt),
            },
        }),
        columnHelper.accessor('usage', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(voucherTypesMessages.usage),
                badgeOptions: VOUCHER_USAGE_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.amount && row.original.currency
                    ? formatNumber(row.original.amount / 100, {
                          style: 'currency',
                          currency: row.original.currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.accessor('remaining_amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.remaining_amount && row.original.currency
                    ? formatNumber(row.original.remaining_amount / 100, {
                          style: 'currency',
                          currency: row.original.currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(vouchersMessages.remainingAmount),
            },
        }),
        columnHelper.accessor('percent', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const percent = row.original.percent;
                const displayTaxPercent = (Number(percent) * 100).toFixed(2);
                return percent != null ? `${displayTaxPercent}%` : '-';
            },
            meta: {
                title: formatMessage(discountPoliciesMessages.percent),
            },
        }),
        columnHelper.accessor('issued_by.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(vouchersMessages.issuedBy),
            },
        }),

        columnHelper.accessor('is_used', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(vouchersMessages.isUsed),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellVoucherActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<Voucher, unknown>
            columns={columns}
            data={vouchers}
            semantics={{
                title: formatMessage(voucherTypesMessages.vouchers),
                entryName: formatMessage(voucherTypesMessages.voucher).toLowerCase(),
                entryNames: formatMessage(vouchersMessages.vouchersBadge).toLowerCase(),
                placeholder: formatMessage(voucherTypesMessages.voucher).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageVouchers && { newEntry: CreateVoucherFormModal }),
            }}
        />
    );
};

export default VouchersTable;
